<template>
  <div class="detail-view">
    <div class="detail-view-intro">
      <h1 class="detail-view-title">
        Detalhamento das pendências · {{ agentName }}
      </h1>
    </div>

    <p v-if="loading" class="detail-view-loading">
      Carregando dados das pendências…
    </p>

    <template v-else>
      <div class="detail-stats-label">Reservas</div>
      <div class="detail-stats detail-stats--3col">
        <StatTile
          label="Com pendência"
          :value="totals.detectadas"
          icon="mdi-clipboard-alert-outline"
          color="warning"
        />
        <StatTile
          label="Correção manual"
          :value="totals.grupoManual"
          icon="mdi-account-alert-outline"
          color="error"
        />
        <StatTile
          label="Correção pendente (reservas finalizadas)"
          :value="totals.grupoAgenteFinalizada"
          icon="mdi-clipboard-clock-outline"
          color="secondary"
        />
        <StatTile
          label="Correção pendente (reservas em pendência)"
          :value="totals.grupoAgentePendente"
          icon="mdi-robot-outline"
          color="secondary"
        />
        <StatTile
          label="Resolvidas"
          :value="totals.grupoResolvidas"
          icon="mdi-check-decagram-outline"
          color="success"
        />
        <StatTile
          label="Resolvidas via reimportação"
          :value="totals.grupoResolvidasReimport"
          icon="mdi-file-sync-outline"
          color="success"
        />
      </div>

      <div class="detail-stats-label">Pendências</div>
      <div class="detail-stats detail-stats--4col">
        <StatTile
          label="Total"
          :value="totals.totalPendencias"
          icon="mdi-format-list-numbered"
          color="warning"
        />
        <StatTile
          label="Resolvidas"
          :value="totals.pendenciasResolvidas"
          icon="mdi-check-decagram-outline"
          color="success"
          :badge="`${totals.pendenciasRate}%`"
        />
        <StatTile
          label="Restantes na fila do agente"
          :value="totals.pendenciasRestantesAgente"
          icon="mdi-robot-outline"
          color="secondary"
        />
        <StatTile
          label="Restantes para correção manual"
          :value="totals.pendenciasRestantesManual"
          icon="mdi-account-alert-outline"
          color="error"
        />
      </div>

      <div class="detail-stats-label">
        Saíram da pendência (ação da equipe no Phoenix)
      </div>
      <div class="detail-stats detail-stats--3col">
        <StatTile
          label="Em rascunho agora"
          :value="totals.saiuRascunho"
          icon="mdi-file-document-edit-outline"
          color="warning"
        />
        <StatTile
          label="Finalizadas pela equipe"
          :value="totals.saiuFinalizada"
          icon="mdi-account-check-outline"
          color="success"
        />
        <StatTile
          label="Canceladas no Phoenix"
          :value="totals.saiuCancelada"
          icon="mdi-cancel"
          color="secondary"
        />
      </div>

      <section class="detail-section">
        <div class="detail-section-title">
          Situação das pendências detectadas
        </div>
        <div v-if="totals.totalPendencias" class="progress">
          <div class="progress-bar">
            <div
              v-for="seg in segments"
              :key="seg.key"
              class="progress-seg"
              :class="`progress-seg--${seg.key}`"
              :style="{ width: pct(seg.value) }"
              :title="`${seg.label}: ${seg.value}`"
            >
              <span v-if="segLabelVisible(seg.value)">{{ seg.value }}</span>
            </div>
          </div>
          <div class="progress-legend">
            <span
              v-for="seg in segments"
              :key="seg.key"
              class="progress-legend-item"
            >
              <span class="dot" :class="`dot--${seg.key}`"></span>
              {{ seg.value }} {{ seg.label }}
            </span>
          </div>
        </div>
        <p v-else class="detail-empty">
          Nenhuma pendência detectada neste período.
        </p>
      </section>

      <section
        v-for="group in groups"
        :key="group.key"
        class="detail-section pendency-group"
      >
        <div
          class="pendency-group-head pendency-group-head--clicavel"
          :class="`pendency-group-head--${group.key}`"
          role="button"
          tabindex="0"
          :aria-expanded="grupoAberto(group.key) ? 'true' : 'false'"
          @click="toggleGrupo(group.key)"
          @keydown.enter.prevent="toggleGrupo(group.key)"
          @keydown.space.prevent="toggleGrupo(group.key)"
        >
          <v-icon
            size="18"
            class="pendency-group-chevron"
            :class="{ 'is-open': grupoAberto(group.key) }"
            >mdi-chevron-right</v-icon
          >
          <div class="pendency-group-head-icon">
            <v-icon size="18" :color="group.iconColor">{{ group.icon }}</v-icon>
          </div>
          <div class="pendency-group-head-text">
            <span class="pendency-group-title">{{ group.title }}</span>
            <span class="pendency-group-sub">{{ group.subtitle }}</span>
          </div>
          <span class="pendency-group-count">{{ contagemGrupo(group) }}</span>
        </div>

        <div
          v-if="group.items.length"
          v-show="grupoAberto(group.key)"
          class="detail-table-wrap"
        >
          <table class="detail-table pendency-table">
            <colgroup>
              <col class="col-chevron" />
              <col class="col-reserva" />
              <col class="col-cliente" />
              <col class="col-chegada" />
              <col class="col-status" />
              <col class="col-email" />
              <col class="col-detectada" />
              <col class="col-resolvida" />
            </colgroup>
            <thead>
              <tr>
                <th></th>
                <th>Reserva</th>
                <th>Cliente</th>
                <th>Chegada</th>
                <th>Situação</th>
                <th>Operação (Azul)</th>
                <th>Detectada</th>
                <th>Resolvida em</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="item in group.items">
                <tr
                  :key="item.id"
                  class="pendency-row"
                  :class="{
                    'is-open': openId === item.id,
                    'pendency-row--manual': item.tratamento === 'manual',
                  }"
                  @click="toggle(item.id)"
                >
                  <td class="chevron-cell">
                    <v-icon
                      size="18"
                      class="chevron"
                      :class="{ 'is-open': openId === item.id }"
                    >
                      mdi-chevron-right
                    </v-icon>
                  </td>
                  <td>
                    <div class="pendency-codes">
                      <span class="pendency-voucher">{{
                        item.localizador || item.codigo
                      }}</span>
                      <span
                        v-if="item.localizador && item.codigo"
                        class="pendency-rec"
                        >{{ item.codigo }}</span
                      >
                    </div>
                  </td>
                  <td class="ellipsis">{{ item.cliente || "—" }}</td>
                  <td>{{ item.dataChegada || "—" }}</td>
                  <td>
                    <span class="tag" :class="`tag--${tagDe(item)}`">{{
                      situacaoLabel(item)
                    }}</span>
                  </td>
                  <td class="operacao-cell">
                    <template v-if="item.acao && item.acao.numero">{{
                      item.acao.numero
                    }}</template>
                    <span
                      v-else-if="item.acao && item.acao.simulado"
                      class="muted"
                      >simulado</span
                    >
                    <span v-else class="muted">—</span>
                    <div
                      v-if="item.acao && item.acao.sombra"
                      class="acao-sombra"
                    >
                      Phoenix sombra · nada gravado
                    </div>
                  </td>
                  <td class="muted">{{ item.detectadaEm }}</td>
                  <td class="muted">{{ quandoSaiu(item) }}</td>
                </tr>
                <tr
                  v-if="openId === item.id"
                  :key="item.id + '-detail'"
                  class="pendency-detail-row"
                >
                  <td colspan="8">
                    <div class="pendency-detail">
                      <div class="pendency-detail-block">
                        <span class="pendency-detail-label"
                          >Solicitação à Azul</span
                        >
                        <p class="pendency-detail-motivo">
                          {{ solicitacaoTexto(item) }}
                        </p>
                        <span class="pendency-detail-label"
                          >Pendência original{{
                            item.pendencyCount ? ` · ${item.pendencyCount}` : ""
                          }}</span
                        >
                        <div
                          v-if="tiposDe(item).length"
                          class="pendency-detail-missing"
                        >
                          <span
                            v-for="t in tiposDe(item)"
                            :key="t"
                            class="chip chip--tipo"
                            >{{ t }}</span
                          >
                        </div>
                        <ul
                          v-if="resumoDe(item).pendencias.length"
                          class="pendency-detail-motivo-list"
                        >
                          <li
                            v-for="(m, mi) in resumoDe(item).pendencias"
                            :key="mi"
                          >
                            {{ m }}
                          </li>
                        </ul>
                        <p v-else class="pendency-detail-motivo">—</p>
                        <p
                          v-if="item.motivoManual"
                          class="pendency-detail-manualnote"
                        >
                          <v-icon size="13" color="#b06a12"
                            >mdi-account-alert-outline</v-icon
                          >
                          {{ item.motivoManual }}
                        </p>
                        <p
                          v-if="item.resposta"
                          class="pendency-detail-manualnote pendency-detail-manualnote--luck"
                        >
                          <v-icon size="13" color="#0f7a6e"
                            >mdi-email-check-outline</v-icon
                          >
                          <span
                            ><strong
                              >Resposta da Azul ({{ item.resposta.ritm }})
                              aplicada no Phoenix:</strong
                            >
                            <template v-for="(c, ci) in item.resposta.campos"
                              ><br :key="'b' + ci" />{{ c.rotulo }}
                              <span :key="'s' + ci"
                                >({{ c.servico }}): {{ c.de }} →
                                {{ c.para }}</span
                              ></template
                            ></span
                          >
                        </p>
                        <p
                          v-if="item.emailAzul"
                          class="pendency-detail-manualnote"
                        >
                          <v-icon size="13" color="#b06a12"
                            >mdi-email-alert-outline</v-icon
                          >
                          <span
                            ><strong
                              >E-mail da Azul ({{ item.emailAzul.ritm }},
                              {{
                                item.emailAzul.recebidoEm ||
                                item.emailAzul.registradoEm
                              }}):</strong
                            >
                            {{ item.emailAzul.resumo }}<br />
                            <em>{{ decisaoEmailLabel(item.emailAzul.decisao) }}</em
                            ><template v-if="item.emailAzul.motivo">
                              — {{ item.emailAzul.motivo }}</template
                            ><template v-if="item.emailAzul.alteradoPor">
                              (alterada por {{ item.emailAzul.alteradoPor
                              }}<template v-if="item.emailAzul.alteradoEm">
                                em {{ item.emailAzul.alteradoEm }}</template
                              >)</template
                            ></span
                          >
                        </p>
                        <p
                          v-if="item.sugestao"
                          class="pendency-detail-manualnote pendency-detail-manualnote--luck"
                        >
                          <v-icon size="13" color="#0f7a6e"
                            >mdi-lightbulb-on-outline</v-icon
                          >
                          <span
                            ><strong>Sugestão para a Luck:</strong>
                            {{ item.sugestao }}</span
                          >
                        </p>
                      </div>

                      <div class="pendency-detail-block">
                        <span class="pendency-detail-label"
                          >O que o agente fez</span
                        >
                        <div
                          v-if="resumoDe(item).feito.length"
                          class="attempt-groups"
                        >
                          <div class="attempt-group">
                            <div class="attempt-group-head">
                              <span
                                class="attempt-badge"
                                :class="`attempt-badge--${
                                  resumoDe(item).badge
                                }`"
                                >{{ resumoDe(item).badgeLabel }}</span
                              >
                              <span v-if="item.acao" class="attempt-group-meta"
                                >execução #{{ item.acao.execId
                                }}<template v-if="item.acao.registradoEm">
                                  · {{ item.acao.registradoEm }}</template
                                ></span
                              >
                            </div>
                            <ul class="attempt-list">
                              <li
                                v-for="(t, i) in resumoDe(item).feito"
                                :key="i"
                                class="attempt"
                              >
                                <span class="attempt-dot"></span>
                                <span class="attempt-text">{{ t }}</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                        <p v-else class="pendency-detail-none">
                          {{ resumoDe(item).semAcao }}
                        </p>
                        <p
                          v-if="
                            resumoDe(item).falta && resumoDe(item).feito.length
                          "
                          class="pendency-detail-none"
                        >
                          {{ resumoDe(item).falta }}
                        </p>
                        <details
                          v-if="resumoDe(item).tecnicos.length"
                          class="rd-tec"
                        >
                          <summary>Ver evidência</summary>
                          <ul class="rd-tec-lista">
                            <li
                              v-for="(t, i) in resumoDe(item).tecnicos"
                              :key="i"
                            >
                              {{ t }}
                            </li>
                          </ul>
                        </details>
                      </div>

                      <div
                        class="pendency-detail-block pendency-detail-block--meta"
                      >
                        <div>
                          <span class="pendency-detail-label"
                            >Detectada na execução</span
                          >
                          <span class="pendency-detail-meta">
                            {{
                              item.execCriadaId ? `#${item.execCriadaId}` : "—"
                            }}
                            <template v-if="item.detectadaEm"
                              >· {{ item.detectadaEm }}</template
                            >
                          </span>
                        </div>
                        <div>
                          <span class="pendency-detail-label"
                            >Última ação do agente</span
                          >
                          <span class="pendency-detail-meta">
                            {{ item.acao ? `#${item.acao.execId}` : "—" }}
                            <template v-if="item.acao && item.acao.registradoEm"
                              >· {{ item.acao.registradoEm }}</template
                            >
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <p v-else v-show="grupoAberto(group.key)" class="detail-empty">
          {{ group.empty }}
        </p>
      </section>
    </template>
  </div>
</template>

<script>
import { getAzulPendenciesDetail } from "@/services/azulApi.js";
import StatTile from "@/components/StatTile/StatTile.vue";

// Rotulo e cor da etiqueta de cada grupo da aba Detalhamento.
const GRUPO_LABEL = {
  manual: "Correção manual",
  agente_finalizada: "Correção pendente (finalizada)",
  agente_pendente: "Correção pendente",
  resolvida: "Resolvida",
  resolvida_reimport: "Resolvida (reimportação)",
  saiu_rascunho: "Em rascunho (equipe)",
  saiu_finalizada_equipe: "Finalizada pela equipe",
  saiu_cancelada_phoenix: "Cancelada no Phoenix",
};
const GRUPO_LABEL_REGRA = "Resolvida pelo agente";
const GRUPO_LABEL_RESPOSTA = "Resolvida pela resposta da Azul";
// O que o agente fez com o e-mail da Azul que NAO foi aplicado (mesmos rotulos da aba "Respostas da Azul").
const DECISAO_EMAIL_LABEL = {
  simulada: "Simulação: nada foi gravado no Phoenix",
  resolvida_equipe: "Já resolvida pela equipe",
  sem_dado: "Só informativo: não há o que gravar",
  nao_identificado: "O agente não soube aplicar: a equipe precisa conferir",
  sem_reserva: "Reserva indisponível no Phoenix",
  erro: "Erro ao aplicar",
};
const GRUPO_TAG = {
  manual: "manual",
  agente_finalizada: "agente",
  agente_pendente: "agente",
  resolvida: "resolvida",
  resolvida_reimport: "resolvida",
  saiu_rascunho: "agente",
  saiu_finalizada_equipe: "resolvida",
  saiu_cancelada_phoenix: "manual",
};

const EMPTY_TOTALS = {
  detectadas: 0,
  resolvidas: 0,
  resolvidasRate: 0,
  pendenteAgente: 0,
  pendenteManual: 0,
  grupoManual: 0,
  grupoAgenteFinalizada: 0,
  grupoAgentePendente: 0,
  grupoResolvidas: 0,
  grupoResolvidasReimport: 0,
  saiuRascunho: 0,
  saiuFinalizada: 0,
  saiuCancelada: 0,
  pendenciasSairam: 0,
  totalPendencias: 0,
  pendenciasResolvidas: 0,
  pendenciasRestantes: 0,
  pendenciasRestantesAgente: 0,
  pendenciasRestantesManual: 0,
  pendenciasRate: 0,
};

// Mesmo modelo da aba Detalhamento da CVC (mesmos estilos), com os dados da Azul.
export default {
  name: "AzulPendenciesDetail",
  components: { StatTile },
  props: {
    range: { type: Object, required: true },
    agentName: { type: String, default: "Azul" },
    base: { type: String, required: true },
  },
  data() {
    return {
      loading: false,
      totals: EMPTY_TOTALS,
      items: [],
      openId: null,
      // grupos abertos (comecam todos recolhidos); a chave e o "key" do grupo
      gruposAbertos: {},
    };
  },
  computed: {
    // A barra e sobre PENDENCIAS (nao reservas): resolvidas x restantes.
    segments() {
      return [
        {
          key: "resolvida",
          label: "pendências resolvidas",
          value: this.totals.pendenciasResolvidas,
        },
        {
          key: "saiu",
          label: "saíram da pendência (ação da equipe)",
          value: this.totals.pendenciasSairam,
        },
        {
          key: "restante_agente",
          label: "restantes na fila do agente",
          value: this.totals.pendenciasRestantesAgente,
        },
        {
          key: "restante_manual",
          label: "restantes para correção manual",
          value: this.totals.pendenciasRestantesManual,
        },
      ].filter((s) => s.value > 0);
    },
    groups() {
      const by = (g) => this.items.filter((i) => i.grupo === g);
      return [
        {
          key: "manual",
          icon: "mdi-account-alert-outline",
          iconColor: "#b06a12",
          title: "Correção manual",
          subtitle:
            "O agente não foi à Azul: alguém precisa ajustar no Phoenix (ex.: composição do serviço, apelido do hotel)",
          items: by("manual"),
          empty: "Nenhuma pendência precisando de correção manual. 🎉",
        },
        {
          key: "agente_finalizada",
          icon: "mdi-clipboard-clock-outline",
          iconColor: "#2f7fd1",
          title: "Correção pendente — reservas finalizadas",
          subtitle:
            "A Azul foi informada (operação abaixo); o agente anotou no serviço, preencheu os campos obrigatórios com “NÃO INFORMADO” e concluiu a reserva. Finalizada não significa correta: os dados são fictícios e a pendência continua até a correção real",
          items: by("agente_finalizada"),
          empty: "Nenhuma reserva finalizada pelo agente neste período.",
        },
        {
          key: "agente_pendente",
          icon: "mdi-robot-outline",
          iconColor: "#2f7fd1",
          title: "Correção pendente — reservas em pendência",
          subtitle:
            "Dado que faltou na planilha da Azul. A reserva segue em pendência (ainda não tratada, ou não pôde ser concluída — abra a linha para ver o motivo)",
          items: by("agente_pendente"),
          empty: "Nenhuma reserva aguardando o agente.",
        },
        {
          key: "resolvida",
          icon: "mdi-check-circle-outline",
          iconColor: "#0f7a6e",
          title: "Resolvidas",
          subtitle:
            "Reservas que saíram de “Pendência de Importação” no Phoenix e foram resolvidas (pelo agente, com a evidência da planilha, ou em uma execução posterior)",
          items: by("resolvida"),
          empty: "Nenhuma pendência resolvida neste período.",
        },
        {
          key: "resolvida_reimport",
          icon: "mdi-file-sync-outline",
          iconColor: "#0f7a6e",
          title: "Resolvidas via reimportação",
          subtitle:
            "A equipe corrigiu o motivo (por exemplo, cadastrou o voo) e, ao reimportar a planilha, a reserva voltou ao Phoenix sem pendência. O Phoenix apaga e recria a pendente, então a pendência some sozinha.",
          items: by("resolvida_reimport"),
          empty: "Nenhuma pendência resolvida via reimportação neste período.",
        },
        {
          key: "saiu_finalizada_equipe",
          icon: "mdi-account-check-outline",
          iconColor: "#0f7a6e",
          title: "Finalizadas pela equipe no Phoenix",
          subtitle:
            "Saíram de “Pendência de Importação” por ação de uma pessoa (não do agente). Aparece quem editou por último.",
          items: by("saiu_finalizada_equipe"),
          empty: "Nenhuma reserva finalizada pela equipe neste período.",
        },
        {
          key: "saiu_rascunho",
          icon: "mdi-file-document-edit-outline",
          iconColor: "#b06a12",
          title: "Em rascunho no Phoenix",
          subtitle:
            "Saíram da pendência e estão em Rascunho agora (a equipe ainda não finalizou).",
          items: by("saiu_rascunho"),
          empty: "Nenhuma reserva em rascunho.",
        },
        {
          key: "saiu_cancelada_phoenix",
          icon: "mdi-cancel",
          iconColor: "#6b7580",
          title: "Canceladas no Phoenix",
          subtitle: "Saíram da pendência porque a equipe cancelou a reserva.",
          items: by("saiu_cancelada_phoenix"),
          empty: "Nenhuma reserva cancelada pela equipe neste período.",
        },
      ];
    },
  },
  watch: {
    range() {
      this.load();
    },
    base() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    grupoAberto(key) {
      return !!this.gruposAbertos[key];
    },
    toggleGrupo(key) {
      this.$set(this.gruposAbertos, key, !this.gruposAbertos[key]);
    },
    async load() {
      this.loading = true;
      this.openId = null;
      try {
        const data = await getAzulPendenciesDetail(this.range, this.base);
        this.totals = data.totals;
        this.items = data.items;
      } catch (err) {
        console.error("Falha ao carregar o detalhamento das pendências", err);
        this.totals = EMPTY_TOTALS;
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    decisaoEmailLabel: (d) => DECISAO_EMAIL_LABEL[d] || d,
    situacaoLabel(item) {
      if (item.grupo === "resolvida" && item.resolvidaPelaResposta)
        return GRUPO_LABEL_RESPOSTA;
      if (
        item.grupo === "resolvida" &&
        item.acao &&
        item.acao.resultado === "resolvida_regra"
      )
        return GRUPO_LABEL_REGRA;
      return GRUPO_LABEL[item.grupo] || item.grupo;
    },
    tagDe(item) {
      return GRUPO_TAG[item.grupo] || "agente";
    },
    // Quando a reserva foi RESOLVIDA. A "finalizada" pelo agente nao conta: ela segue pendente (dados ficticios).
    // Resolvida por regra: a hora em que o agente concluiu.
    quandoSaiu(item) {
      if (item.saida)
        return item.saida.atualizadoEm || item.saida.detectadaEm || "—";
      if (item.grupo === "resolvida_reimport")
        return item.reimport ? item.reimport.em : item.resolvidaEm || "—";
      if (item.grupo !== "resolvida") return "—";
      if (item.resolvidaPelaResposta && item.resposta)
        return item.resposta.registradoEm;
      return item.acao && item.acao.resultado === "resolvida_regra"
        ? item.acao.registradoEm
        : item.resolvidaEm;
    },
    regraTexto(r) {
      const nomes = {
        reserva_cancelada: "reserva já cancelada no Phoenix",
        hoteis: "mais de um hotel na planilha",
        grupos: "serviços repetidos por grupo de passageiros (unificados)",
        voo_web: "voo de ida pelos dados da planilha (WEB-IDA)",
        horario_voo: "horário do voo divergente do cadastro",
        veiculo: "tipo de veículo definido pelo número de passageiros",
        data_out:
          "data do OUT ajustada (IN e OUT no mesmo dia, sem retorno na planilha)",
        apelido_hotel:
          "hotel sem apelido (o agente cadastra o apelido quando tem confiança alta)",
      };
      return String(r)
        .split("+")
        .map((x) => nomes[x] || x)
        .join(" + ");
    },
    evidenciaDe(item) {
      const e = item.acao && item.acao.evidencia;
      return e && Array.isArray(e.linhas) ? e.linhas : [];
    },
    // Resumo curto e organizado de uma reserva: o que era, o que o agente fez, o que falta. O resto vai em "Detalhes técnicos".
    // Etiquetas de tipo (uma por tipo de pendencia), como os chips do detalhe da CVC.
    // Mensagens da pendencia COMO FORAM DETECTADAS (a regra do agente tira de `mensagens` as que corrigiu; sem esta, uma reserva que o
    // agente resolveu por inteiro ficava com "Pendência original" vazia).
    msgsOriginais(item) {
      return item.mensagensOriginais && item.mensagensOriginais.length
        ? item.mensagensOriginais
        : item.mensagens || [];
    },
    tiposDe(item) {
      return [
        ...new Set(this.msgsOriginais(item).map((m) => m.label).filter(Boolean)),
      ];
    },
    solicitacaoTexto(item) {
      const a = item.acao || null;
      if (a && a.formStatus === "enviada")
        return `Enviada — solicitação ${a.numero || "sem número"}.`;
      if (a && a.formStatus === "falha")
        return "Falha ao enviar: a Azul não foi informada.";
      if (a && a.formStatus === "simulada")
        return "Simulada: preenchida, mas não enviada.";
      if (a && a.resultado === "resolvida_regra")
        return "Não precisou: resolvida pelo agente.";
      if (item.grupo === "manual")
        return "Não enviada — correção manual, a Azul não é acionada.";
      return "Sem solicitação para esta reserva.";
    },
    // Saiu da Pendencia por acao da equipe: o resumo diz o que aconteceu e QUEM editou por ultimo.
    resumoDaSaida(item) {
      const sd = item.saida;
      const quem = sd.atualizadoPor
        ? ` — última edição de ${sd.atualizadoPor}${
            sd.atualizadoEm ? " em " + sd.atualizadoEm : ""
          }`
        : "";
      const o = {
        finalizada_equipe: "Finalizada no Phoenix pela equipe",
        rascunho: "Está em Rascunho no Phoenix (saiu da Pendência)",
        cancelada_phoenix: "Cancelada no Phoenix",
      }[sd.destino];
      return {
        feito: [`${o}${quem}${sd.phoenixCode ? " · " + sd.phoenixCode : ""}`],
        badgeLabel: "Ação da equipe",
      };
    },
    resumoDe(item) {
      if (item.saida) {
        const r = this.resumoDaSaida(item);
        const msgs0 = this.msgsOriginais(item);
        return {
          pendencias: [
            ...new Set(msgs0.map((m) => m.label || m.texto).filter(Boolean)),
          ],
          feito: r.feito,
          falta: "",
          tecnicos: [],
          badge: "concluida",
          badgeLabel: r.badgeLabel,
          semAcao: "",
        };
      }
      const a = item.acao || null;
      const msgs = this.msgsOriginais(item);
      // pendencias: uma linha por TIPO (sem repetir IN/OUT nem origem/destino do mesmo hotel)
      const porCat = new Map();
      for (const m of msgs) {
        const c = m.categoria || "outra";
        if (!porCat.has(c))
          porCat.set(c, { label: m.label || m.texto, textos: [] });
        porCat.get(c).textos.push(String(m.texto || ""));
      }
      const colchete = (t) => (/\[([^\]]+)\]/.exec(t) || [])[1];
      const pendencias = [];
      for (const [cat, g] of porCat) {
        if (
          cat === "sem_servico_composicao" &&
          porCat.has("composicao_nao_encontrada")
        )
          continue;
        const dirs = [
          ...new Set(
            g.textos
              .map((t) => (/voo (in|out)/i.exec(t) || [])[1])
              .filter(Boolean)
              .map((d) => d.toUpperCase())
          ),
        ];
        if (
          [
            "voo_nao_informado",
            "data_voo_nao_informada",
            "voo_nao_encontrado",
            "voo_nao_cadastrado_dia",
            "horario_voo",
          ].includes(cat)
        ) {
          const base = {
            voo_nao_informado: "não informado na planilha",
            data_voo_nao_informada: "sem data na planilha",
            voo_nao_encontrado: "fora do cadastro de voos",
            voo_nao_cadastrado_dia: "não cadastrado para o dia da semana",
            horario_voo: "com horário diferente do cadastro",
          }[cat];
          pendencias.push(
            `Voo ${dirs.join(" e ") || ""} ${base}`.replace(/\s+/g, " ")
          );
        } else if (cat === "hotel_nao_encontrado") {
          const nomes = [...new Set(g.textos.map(colchete).filter(Boolean))];
          pendencias.push(
            `Hotel sem apelido no Phoenix${
              nomes.length ? `: ${nomes.join(", ")}` : ""
            }`
          );
        } else if (cat === "composicao_nao_encontrada") {
          const id = (/#\s*id produto\s*\[(\d+)\]/i.exec(g.textos[0]) || [])[1];
          pendencias.push(
            `Composição não cadastrada${id ? ` (produto ${id})` : ""}`
          );
        } else if (cat === "mais_de_um_hotel") {
          const nomes = (g.textos[0].match(/\[[^\]]+\]/g) || []).map((x) =>
            x.slice(1, -1)
          );
          pendencias.push(
            `Mais de um hotel na planilha${
              nomes.length ? `: ${[...new Set(nomes)].join(" / ")}` : ""
            }`
          );
        } else {
          pendencias.push(g.label);
        }
      }

      // o que o agente fez (so o que importa, uma linha cada)
      const feito = [];
      const ev =
        a && a.evidencia && Array.isArray(a.evidencia.linhas)
          ? a.evidencia.linhas
          : [];
      const regras = a && a.regra ? String(a.regra).split("+") : [];
      const sim = a && a.simulado ? " (simulação)" : "";
      // O agente avaliou a reserva e NAO agiu (sem certeza): registra qual regra olhou e por que parou.
      if (a && a.resultado === "pendente" && !a.simulado && a.motivo) {
        const NOME_REGRA = {
          voo_web: "voo pela planilha",
          grupos: "unificação de serviços repetidos",
          hoteis: "hotéis pela planilha",
          horario_voo: "horário do voo",
          veiculo: "tipo de veículo",
          data_out: "data do OUT",
          apelido_hotel: "apelido de hotel",
          reserva_cancelada: "reserva cancelada",
          chamado_existente: "chamado já aberto na Azul",
        };
        const nomes = regras.map((r) => NOME_REGRA[r] || r).filter(Boolean);
        feito.push(
          `Avaliada${
            nomes.length ? ` (${nomes.join(", ")})` : ""
          }, sem alteração: ${a.motivo}`
        );
      }
      if (a && !(a.resultado === "pendente" && !a.simulado)) {
        if (regras.includes("voo_web")) {
          // So conta como "voo de ida aplicado" quando a regra TEVE certeza: so nesse caso a evidencia traz a linha do
          // cadastro. Se ela ficou em duvida (voo fora do cadastro, horario muito diferente), a evidencia vem vazia.
          const f = ev
            .map((l) => /Cadastro do Phoenix: (\S+ - \d+)/.exec(l))
            .find(Boolean);
          if (f) {
            feito.push(
              `Voo de ida${sim}: ${f[1]} (planilha conferida com o cadastro)`
            );
          }
        }
        if (regras.includes("data_out")) {
          const ac = (a.camposAlterados || []).find(
            (c) => c && c.rotulo === "Data de execução do OUT"
          );
          if (ac) {
            feito.push(
              `Data do OUT${sim}: de ${ac.de} para ${ac.para} (data fictícia, só para tirar a duplicidade com o IN)`
            );
          }
        }
        // horario do voo divergente do cadastro: sinalizacao em destaque (a reserva foi concluida mesmo assim)
        ev.filter((l) => /^ATENÇÃO:/.test(l)).forEach((l) => feito.push(l));
        if (regras.includes("reserva_cancelada")) {
          const cc = ((a.evidencia && a.evidencia.canceladas) || [])
            .map((c) => c.code)
            .filter(Boolean);
          feito.push(
            `Reserva cancelada no Phoenix${
              cc.length ? ` (${cc.join(", ")})` : ""
            } confirmada${sim}`
          );
        }
        if (regras.includes("hoteis")) {
          const n = (a.camposAlterados || []).length;
          feito.push(
            `Hotéis ajustados pela planilha${
              n ? ` (${n} alteração${n > 1 ? "ões" : ""})` : ""
            }${sim}`
          );
        }
        if (regras.includes("grupos"))
          feito.push(`Serviços repetidos por grupo unificados${sim}`);
        if (a.resultado === "finalizada") {
          const ficticios = (a.camposAlterados || []).filter((c) =>
            /NÃO INFORMADO/i.test(c.para || "")
          );
          if (ficticios.length) {
            const nome = (c) =>
              String(c.rotulo || "campo")
                .replace(/^Estabelecimento de /i, "hotel de ")
                .toLowerCase() +
              (c.servico_nome
                ? ` (${String(c.servico_nome).split(" ")[0]})`
                : "");
            feito.push(
              `Concluída com dado fictício: ${[
                ...new Set(ficticios.map(nome)),
              ].join(", ")}`
            );
          } else feito.push("Concluída com dados fictícios");
        }
        if (a.resultado === "resolvida_regra" && !feito.length)
          feito.push("Resolvida pelo agente");
      }

      // o que falta
      let falta = null;
      if (item.grupo === "agente_finalizada")
        falta =
          "Aguardando a Azul responder para trocar os dados fictícios pelos reais.";
      else if (item.grupo === "agente_pendente")
        falta = (a && a.motivo) || "Na fila do agente.";
      else if (item.grupo === "manual")
        falta =
          item.motivoManual || (a && a.motivo) || "Correção manual no Phoenix.";

      // detalhes tecnicos (fechados por padrao)
      const tecnicos = [];
      ev.forEach((l) => tecnicos.push(l));
      if (a) {
        if (a.formStatus || a.numero)
          tecnicos.push(`Formulário da Azul: ${this.formularioTexto(a)}`);
        if (a.formStatus || a.observacaoGravada !== null)
          tecnicos.push(`Observação no Phoenix: ${this.observacaoTexto(a)}`);
        (a.camposAlterados || []).forEach((c) =>
          tecnicos.push(
            `${c.rotulo} (${c.servico_nome || `serviço ${c.servico_id}`}): ${
              c.de || "vazio"
            } → ${c.para}${c.simulada ? " · simulado" : ""}`
          )
        );
        if (a.motivo) tecnicos.push(`Motivo: ${a.motivo}`);
      }
      const det = [
        item.execCriadaId
          ? `detectada na execução #${item.execCriadaId}${
              item.detectadaEm ? ` (${item.detectadaEm})` : ""
            }`
          : null,
        item.execResolvidaId
          ? `resolvida na execução #${item.execResolvidaId}${
              item.resolvidaEm ? ` (${item.resolvidaEm})` : ""
            }`
          : null,
      ].filter(Boolean);
      if (det.length) tecnicos.push(det.join(" · "));
      // evidencia: so as linhas que explicam a decisao do agente (o resto fica no log da execucao)
      const badge = !a
        ? "sem_conclusao"
        : a.resultado === "resolvida_regra"
        ? "concluida"
        : a.resultado === "finalizada"
        ? "parcial"
        : a.resultado === "erro"
        ? "falhou"
        : "sem_conclusao";
      const badgeLabel = {
        concluida: "Resolvida pelo agente",
        parcial: "Concluída com dado fictício",
        falhou: "Erro",
        sem_conclusao: a && a.simulado ? "Simulação" : "Sem conclusão",
      }[badge];
      const semAcao =
        item.grupo === "manual"
          ? "O agente não atua nesta pendência (correção manual)."
          : "O agente ainda não tratou esta reserva.";
      return {
        pendencias,
        feito,
        falta,
        tecnicos: ev,
        badge,
        badgeLabel,
        semAcao,
      };
    },
    formularioTexto(a) {
      if (a.formStatus === "enviada")
        return `enviado — solicitação ${a.numero}${
          a.assunto ? ` (assunto: ${a.assunto})` : ""
        }`;
      if (a.formStatus === "simulada")
        return "simulado: preenchido, mas não enviado";
      if (a.formStatus === "falha") return "falhou — a Azul não foi informada";
      return a.numero ? `solicitação ${a.numero}` : "não enviado";
    },
    observacaoTexto(a) {
      if (a.sombra && a.observacaoGravada)
        return `SIMULADA (Phoenix sombra — nada foi gravado no Phoenix real): “${a.observacaoTexto}”`;
      if (a.observacaoGravada && a.observacaoTexto)
        return `gravada no primeiro serviço: “${a.observacaoTexto}”`;
      if (a.simulado) return "simulado: nada foi gravado";
      return "não gravada";
    },
    resultadoTexto(a) {
      if (a.resultado === "resolvida_regra")
        return "reserva concluída pelo agente com dados corretos (confirmados pela planilha) — saiu de Pendência de Importação";
      if (a.resultado === "finalizada" && a.sombra)
        return "SIMULADO (Phoenix sombra): o agente concluiria a reserva, mas nada foi alterado no Phoenix real — ela continua em Pendência de Importação";
      if (a.resultado === "finalizada")
        return "reserva concluída no Phoenix com dados fictícios (“NÃO INFORMADO”) — continua contada como pendência até a Azul responder";
      if (a.resultado === "simulada")
        return "simulação: nada foi alterado no Phoenix";
      if (a.resultado === "pendente")
        return `segue em pendência — ${a.motivo || "não pôde ser concluída"}${
          a.evidencia &&
          a.evidencia.faltaApelido &&
          a.evidencia.faltaApelido.length
            ? ` (cadastrar o apelido: ${a.evidencia.faltaApelido.join(", ")})`
            : ""
        }`;
      if (a.resultado === "erro")
        return `erro ao tratar a reserva — ${
          a.motivo || "ver a linha do tempo da execução"
        }`;
      return "ainda não tratada no Phoenix";
    },
    // Categorias distintas (rotulo curto) das mensagens da reserva.
    categoriasDe(item) {
      return [...new Set(item.mensagens.map((m) => m.label).filter(Boolean))];
    },
    // Cabecalho de cada lista: quantas RESERVAS e quantas PENDENCIAS (uma reserva pode ter varias).
    // Listas abertas contam o que ainda falta resolver; a de resolvidas, o que foi resolvido.
    contagemGrupo(group) {
      const aberta = group.key === "manual" || group.key === "agente_pendente";
      const saiu = String(group.key).startsWith("saiu_");
      const reservas = group.items.length;
      const campo = saiu
        ? "pendSairam"
        : aberta
        ? "pendRestantes"
        : "pendResolvidas";
      const pendencias = group.items.reduce(
        (s, i) => s + (Number(i[campo]) || 0),
        0
      );
      const rReserva = saiu
        ? reservas === 1
          ? "reserva"
          : "reservas"
        : aberta
        ? reservas === 1
          ? "reserva pendente"
          : "reservas pendentes"
        : reservas === 1
        ? "reserva resolvida"
        : "reservas resolvidas";
      return `${reservas} ${rReserva} · ${pendencias} ${
        pendencias === 1 ? "pendência" : "pendências"
      }`;
    },
    toggle(id) {
      this.openId = this.openId === id ? null : id;
    },
    pct(n) {
      if (!this.totals.totalPendencias) return "0%";
      return `${(n / this.totals.totalPendencias) * 100}%`;
    },
    segLabelVisible(n) {
      return (
        this.totals.totalPendencias && n / this.totals.totalPendencias > 0.06
      );
    },
  },
};
</script>

<style src="../PendenciesDetail/styles.scss" scoped lang="scss" />
<style scoped lang="scss">
// 5 cards de reservas (uma por lista da aba).
.detail-stats--5col {
  grid-template-columns: repeat(5, 1fr);

  @media (max-width: 1300px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 760px) {
    grid-template-columns: repeat(2, 1fr);
  }
}
.operacao-cell {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.acao-sombra {
  font-size: 0.68rem;
  font-weight: 600;
  color: #b06a12;
  margin-top: 2px;
}
.acao-agente {
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px dashed #d6dbe0;
}
.acao-lista {
  margin: 6px 0 0;
  padding-left: 18px;
  font-size: 0.82rem;
  line-height: 1.5;
}
.acao-campos {
  margin: 2px 0 0;
  padding-left: 16px;
}
.acao-de {
  color: #8a929b;
  font-style: italic;
}
// Bloco de 4 cards (reservas e pendencias), igual ao da CVC.
.detail-stats--4col {
  grid-template-columns: repeat(4, 1fr);

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
}
// Barra e legenda: restantes divididas por quem trata.
.progress-seg--saiu,
.dot--saiu {
  background: #8b6fd1;
}
.detail-stats--3col {
  grid-template-columns: repeat(3, 1fr);

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
}
.progress-seg--restante_agente,
.dot--restante_agente {
  background: #2f7fd1;
}
.progress-seg--restante_manual,
.dot--restante_manual {
  background: #d98324;
}
.pendency-group-count {
  font-size: 0.82rem;
  padding: 3px 12px;
  white-space: nowrap;
}
.pendency-detail--azul {
  grid-template-columns: 2fr 1fr;
}
.rd-tec {
  margin-top: 12px;
  font-size: 0.78rem;
  color: #6b7780;
  summary {
    cursor: pointer;
    font-weight: 600;
  }
}
.rd-tec-lista {
  margin: 6px 0 0;
  padding-left: 16px;
  line-height: 1.5;
}
.pendency-detail-manualnote--luck {
  color: #0b5c52;
  background: #e8f4f1;
  border-color: #cfe6e1;
}
.pendency-detail-sugestao {
  margin: 8px 0 0;
  padding: 6px 10px;
  border-radius: 8px;
  background: #e8f4f1;
  color: #0b5c52;
  font-size: 0.82rem;
}
.azul-quem {
  font-size: 0.72rem;
  color: #0f7a6e;
  &.is-manual {
    color: #b4600f;
  }
}
@media (max-width: 900px) {
  .pendency-detail--azul {
    grid-template-columns: 1fr;
  }
}
</style>
